import { z } from 'zod';

export const saleCurrencySchema = z.coerce
  .number()
  .finite()
  .min(0)
  .max(9999999999.99)
  .refine(
    (v) => Math.abs(v * 100 - Math.round(v * 100)) < 0.0001,
    'Use at most two decimal places',
  );

export const salePaymentDraftSchema = z.object({
  amount: z.coerce
    .number()
    .finite()
    .positive('Enter an amount greater than ₹0')
    .max(999999999999.99)
    .refine(
      (v) => Math.abs(v * 100 - Math.round(v * 100)) < 0.0001,
      'Use at most two decimal places',
    ),
  account_id: z.string().uuid('Select a receiving account'),
  payment_method: z.enum(['cash', 'upi', 'bank_transfer', 'cheque', 'other']),
  payment_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Select a valid payment date')
    .refine((v) => {
      const date = new Date(`${v}T12:00:00Z`);
      return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === v;
    }, 'Select a valid payment date'),
  reference_number: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(1000).optional(),
  reason: z.string().trim().max(500).optional(),
  duplicate_confirmed: z.boolean().optional(),
});

export type SalePaymentDraft = z.infer<typeof salePaymentDraftSchema>;

export function paymentBalance(total: number, paid: number, previousAmount = 0) {
  return Math.round((total - paid + previousAmount) * 100) / 100;
}

export function localBusinessDate(timezone = 'Asia/Kolkata') {
  const parts = new Intl.DateTimeFormat('en', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const part = (type: string) => parts.find((p) => p.type === type)?.value;
  return `${part('year')}-${part('month')}-${part('day')}`;
}

export function saleCollectedAmount(
  payments: Array<{ id: string; amount: number | string; payment_type: string; is_void: boolean }>,
  edits: Array<{ payment_id: string; amount: number }> = [],
) {
  const cents = payments
    .filter((p) => !p.is_void)
    .reduce((sum, p) => {
      const amount = edits.find((e) => e.payment_id === p.id)?.amount ?? Number(p.amount);
      return sum + Math.round(amount * 100) * (p.payment_type === 'refund' ? -1 : 1);
    }, 0);
  return cents / 100;
}

/** Keep this key for retries of the same operation; replace it after success. */
export function paymentRequestKey() {
  return `sale-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
}
